import { create } from 'zustand';
import { api } from '@/lib/api';

/**
 * Favoriter.
 *
 * Ägaren 2026-09-24: "ta bort att man måste vara inloggad om man lägger det i
 * sina favoriter — det finns ingen mening med det, det sparas i sessionen i
 * stället. Kollade på hur Boozt som ändå säljer enormt mycket hade det."
 *
 * Och han har rätt i varför det spelar roll: att spara en del är ett litet
 * steg som säger "jag är intresserad men inte redo". Att svara på det med en
 * inloggningsruta är att kräva det STÖRSTA steget för det minsta — precis där
 * besökaren är som mest obestämd.
 *
 * Listan bor därför i webbläsaren och fungerar direkt. Loggar kunden in
 * senare slås den lokala listan ihop med kontots, så inget han hunnit spara
 * försvinner för att han skapade konto efteråt. Den lokala listan rensas
 * INTE vid utloggning — favoriterna hör till enheten, och att tömma dem vore
 * att straffa den som loggar ut.
 */
const NYCKEL = 'autofrance-favoriter';

function lasLokalt(): number[] {
  if (typeof window === 'undefined') return [];
  try {
    const ra = localStorage.getItem(NYCKEL);
    const v = ra ? JSON.parse(ra) : [];
    return Array.isArray(v) ? v.filter((n) => Number.isFinite(n)) : [];
  } catch { return []; }
}

function skrivLokalt(ids: Set<number>) {
  try { localStorage.setItem(NYCKEL, JSON.stringify([...ids])); } catch { /* privat läge */ }
}

/** Samma nyckel som ApiClient läser. Inloggad = vi kan spegla mot kontot. */
function arInloggad(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const s = localStorage.getItem('autofrance-auth');
    return !!(s && JSON.parse(s)?.state?.token);
  } catch { return false; }
}

interface WishlistStore {
  ids: Set<number>;
  loaded: boolean;
  /** Läser lokalt direkt, och slår ihop med kontot om någon är inloggad. */
  loadIds: () => Promise<void>;
  toggle: (productId: number) => Promise<boolean>;
  has: (productId: number) => boolean;
  clear: () => void;
  /** Tömmer hela listan — lokalt OCH på kontot om någon är inloggad. */
  tomAlla: () => Promise<void>;
}

export const useWishlistStore = create<WishlistStore>()((set, get) => ({
  ids: new Set(),
  loaded: false,

  loadIds: async () => {
    const lokala = new Set(lasLokalt());
    // Lokalt FÖRST och synkront: hjärtat ska vara ifyllt när sidan ritas,
    // inte blinka till efter ett nätanrop.
    set({ ids: lokala, loaded: true });

    if (!arInloggad()) return;
    try {
      const serverIds = await api.getWishlistIds();
      const sammanslagen = new Set<number>([...serverIds, ...lokala]);

      /* Det kunden sparat innan han loggade in skickas upp. Utan det
         försvinner favoriterna i samma sekund han skapar konto — alltså
         tvärtemot poängen med att de fungerar utan konto. */
      const saknasPaServern = [...lokala].filter((id) => !serverIds.includes(id));
      await Promise.all(saknasPaServern.map((id) => api.addToWishlist(id).catch(() => null)));

      set({ ids: sammanslagen });
      skrivLokalt(sammanslagen);
    } catch {
      // Nätfel eller utgången token — den lokala listan gäller ändå.
    }
  },

  toggle: async (productId: number) => {
    const current = get().ids;
    const fanns = current.has(productId);
    const next = new Set(current);
    if (fanns) next.delete(productId); else next.add(productId);

    // Lokalt är sanningen och sker omedelbart; kontot är en spegling.
    set({ ids: next });
    skrivLokalt(next);

    if (arInloggad()) {
      try {
        if (fanns) await api.removeFromWishlist(productId);
        else await api.addToWishlist(productId);
      } catch {
        /* Rullar INTE tillbaka. Kunden har sett hjärtat fyllas, och att ta
           bort det för att vårt nätanrop misslyckades vore att straffa honom
           för vårt fel. Listan ligger kvar lokalt och synkas vid nästa
           laddning. */
      }
    }
    return !fanns;
  },

  has: (productId: number) => get().ids.has(productId),

  tomAlla: async () => {
    const fanns = [...get().ids];
    set({ ids: new Set() });
    skrivLokalt(new Set());
    if (arInloggad()) {
      // Kontot ska stämma med det kunden ser. Ett misslyckat anrop på en
      // enskild rad får inte stoppa de övriga.
      await Promise.all(fanns.map((id) => api.removeFromWishlist(id).catch(() => null)));
    }
  },

  /* Används vid utloggning. Tömmer BARA minnet av kontots lista — den lokala
     listan läses in igen, så det kunden sparat på sin egen telefon finns
     kvar. */
  clear: () => {
    const lokala = new Set(lasLokalt());
    set({ ids: lokala, loaded: true });
  },
}));
